"use client";

const accent = "text-[#ff5c00]";

type ServiceCard = {
  title: string;
  body: string;
};

const topRowServices: ServiceCard[] = [
  {
    title: "Social Marketing",
    body: "Hi Ho creates and develops comprehensive social campaigns that begin with strong creative and evolve through data-driven optimisation across every stage of delivery. We design concepts that capture attention, then use real-time performance data from impressions, clicks, and engagement to refine messaging, formats, and audiences across Meta (Facebook and Instagram), TikTok, and LinkedIn. By continuously testing, learning, and adjusting, we ensure your brand stays relevant, visible, and compelling in fast-moving social feeds. The result is a living campaign system that keeps your message fresh, your audience engaged, and your performance improving over time.",
  },
  {
    title: "AI & Search Engine Optimisation",
    body: "Hi Ho plans and delivers comprehensive search strategies that combine search engine optimisation with dedicated AI and answer-based search approaches, ensuring brands are visible across both traditional search results and AI-driven discovery platforms. We tailor each strategy to a client's goals, market, and competitive landscape, focusing on technical SEO, content structure, and authority building. Through ongoing optimisation, keyword performance analysis, and answer-focused content frameworks, we improve how brands are discovered, understood, and selected by both users and AI systems. By integrating structured data, FAQs, and intent-led content, Hi Ho builds scalable search ecosystems that drive consistent traffic, increase trust, and support long-term growth in an evolving search landscape.",
  },
  {
    title: "Google Ads",
    body: "Hi Ho plans and manages high-performance Google Ads, Google Shopping, Display, and Discovery campaigns designed to drive measurable growth across every stage of the customer journey. We tailor each campaign to a client's goals, products, and market, combining smart targeting, compelling creative, and data-led optimisation. From search and shopping feeds to visual display and discovery placements, we continuously test audiences, messaging, and formats to improve efficiency and scale results. Through ongoing performance analysis, bid management, and feed optimisation, Hi Ho builds advertising systems that increase visibility, drive qualified traffic, and deliver consistent, profitable returns over time.",
  },
];

const bottomRowServices: ServiceCard[] = [
  {
    title: "Direct Messaging Marketing",
    body: "Hi Ho designs and delivers comprehensive Direct Message Marketing campaigns using email and SMS, building connected customer journeys that support growth across the entire lifecycle. We create strategies that are tailored to each client's goals, audience, and data, ensuring every message is timely, relevant, and performance-driven. From welcome and onboarding flows to abandoned cart recovery, post-purchase engagement, and reactivation campaigns, we map and automate communication that strengthens relationships and drives revenue. Through smart segmentation, behavioural triggers, and personalised content, Hi Ho extends messaging beyond one-off sends into scalable, always-on systems. The result is a lifecycle marketing engine that improves retention, increases lifetime value, and turns customers into repeat buyers and brand advocates.",
  },
  {
    title: "Marketing Automation",
    body: "Hi Ho builds intelligent digital marketing automation systems that connect with customers across all major digital channels, creating consistent, data-driven communication at every stage of the journey. We design strategies that integrate email, SMS, social, search, and on-site experiences, ensuring each interaction is timely, relevant, and aligned to customer intent. By using behavioural triggers, segmentation, and real-time data, we turn marketing into an always-on engine that responds to how people actually engage with your brand. The result is a scalable automation framework that improves efficiency, strengthens relationships, and drives measurable growth across the entire digital ecosystem.",
  },
];

export default function ConversionFunnel() {
  return (
    <section
      className="relative bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:pt-20 md:pb-28"
      aria-labelledby="conversion-funnel-heading"
    >
      <div className="site-content">
        <div className="mt-[clamp(3rem,18vw,31.25rem)] grid grid-cols-1 gap-8 lg:mt-[clamp(26rem,26vw,42rem)] lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[650px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              Conversion funnel development
            </p>
            <h2
              id="conversion-funnel-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              take your customers on a journey that
              <span className={accent}> ends in conversion</span> time and
              time again
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p
              className={`text-4xl font-medium leading-none md:text-5xl ${accent}`}
              aria-hidden
            >
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              Every conversion starts with a journey,
              your customer&apos;s needs, questions, and
              decisions, and our job is to guide them
              through it smoothly. We map each step,
              remove friction, clarify choices, and
              design experiences that build trust and
              momentum so visitors don&apos;t just browse,
              they move forward with confidence and
              arrive ready to act.
            </p>
          </div>
        </div>

        {/* Services */}
        <div className="mt-16 border-t border-[#231f20]/40 pt-10 md:mt-20 md:pt-14">
          <div className="grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-20 lg:gap-y-20">
            {topRowServices.map((service) => (
              <article key={service.title}>
                <p className={`text-3xl leading-none md:text-4xl ${accent}`} aria-hidden>
                  *
                </p>
                <h3 className={`mt-5 text-3xl font-medium leading-[1.05] ${accent}`}>
                  {service.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed text-[#231f20]/70 md:text-lg">
                  {service.body}
                </p>
              </article>
            ))}

            <div className="self-start pt-12 md:pt-14">
              <h3 className="text-5xl font-medium leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">
                Be where
                <br />
                the <span className={accent}>people</span>
                <br />
                are!
              </h3>
            </div>

            {bottomRowServices.map((service) => (
              <article key={service.title}>
                <p className={`text-3xl leading-none md:text-4xl ${accent}`} aria-hidden>
                  *
                </p>
                <h3 className={`mt-5 text-3xl font-medium leading-[1.05] ${accent}`}>
                  {service.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
                  {service.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
